import React, { useMemo, useState, useEffect } from 'react';
import { Controller, useForm } from 'react-hook-form';
import NumberFormat from 'react-number-format';

import { Card } from 'primereact/card';
import { AutoComplete } from 'primereact/autocomplete';
import { Tag } from 'primereact/tag';
import { Button } from 'primereact/button';
import { Dropdown } from 'primereact/dropdown';
import { InputText } from 'primereact/inputtext';
import { Tooltip } from 'primereact/tooltip';
import { InputSwitch } from 'primereact/inputswitch';

import ImageComponent from 'shared/ImageComponent';
import { handleRedirection } from 'utils/utils';
import InputErrorMessage from 'components/UI/InputErrorMessage/InputErrorMessage';
import CustomFields from 'modules/lead/AddLead/components/ContactForm/CustomFields';
import { generateFullName } from 'utils/generateFullNameUtils';
import { useToggle } from 'hooks/useToggle';
import { useContacts } from 'hooks/useContacts';
import { useCaseContact } from 'modules/cases/hooks/useCaseContact';
import { handleSettingSkAndPkToLocalStorage } from 'utils/localStorageUtils';
import { useHistory } from 'react-router-dom';
import { countriesList, stateList } from 'constants/stateList';
import { formatDOB, parseDOB, validatePhoneNumber } from 'modules/lead/helpers/helpers';
import { emailDuplicateCheck } from 'services/Contact/contactServices';
import TextSnippetWrapper from 'components/TextSnippetWrapper';
import { Calendar } from 'primereact/calendar';

const state_list = stateList?.map((index) => {
  return {
    label: index.abbreviation + ' - ' + index.name,
    value: index.abbreviation + ' - ' + index.name,
  };
});

const itemTemplate = (e) => generateFullName(e);

export default function Client({ caseDetails, isLeadSpecific, getDetails }) {
  const [isEdit, toggleIsEdit] = useToggle(false);
  const [client, setClient] = useState();
  const { contactList, searchAsync, loading } = useContacts();
  const history = useHistory();
  const {
    control,
    watch,
    handleSubmit,
    formState: { errors, dirtyFields, isSubmitting, isValidating, isDirty },
    setValue,
    trigger,
    reset,
  } = useForm();
  const {
    customFields: customFieldList,
    loadCustomFields,
    changeClient,
    handleUpdateContact,
  } = useCaseContact({ caseDetails, getDetails, isLeadSpecific });

  const isArchivedDisabled = caseDetails?.client_details?.some((item) => item?.status === 'ARCHIVED');

  const clientDetails = useMemo(() => {
    if (caseDetails?.client_details?.length === 1) {
      return caseDetails?.client_details[0];
    } else {
      return caseDetails?.client_details?.find((data) => data?.is_primary_contact === true);
    }
  }, [caseDetails]);

  const customFields = useMemo(() => {
    return clientDetails?.custom_field
      ?.filter((v) => v?.value)
      ?.map((c) => {
        let item2 = customFieldList?.find((item) => item?.custom_field_id === c?.custom_field_id);
        if (item2) {
          c.position = item2.position;
        }
        return c;
      })
      ?.sort((a, b) => a?.position - b?.position);
  }, [clientDetails, customFieldList]);

  const gotoContacts = (e) => {
    const { contact_id, sk, pk } = clientDetails;
    const url = contact_id ? `/contacts/${contact_id}` : '/contacts';
    e.preventDefault();

    handleRedirection(history, url, clientDetails);
    if (sk && pk) handleSettingSkAndPkToLocalStorage(sk, pk);
  };
  useEffect(() => {
    loadCustomFields();
  }, []);

  useEffect(() => {
    if (clientDetails) {
      reset({ ...clientDetails, custom_field: customFields, country: clientDetails?.country ?? 'United States' });
    }
  }, [clientDetails, customFields]);

  const handleEditClient = () => {
    setClient(
      clientDetails?.first_name ? (clientDetails?.first_name ?? '') + ' ' + (clientDetails?.last_name ?? '') : clientDetails?.title
    );
    toggleIsEdit();
  };

  const updateCaseContact = () => {
    changeClient(client, clientDetails?.contact_id, isLeadSpecific).then(() => {
      setClient('');
      toggleIsEdit();
    });
  };

  const validatePhone = async (value) => {
    if (clientDetails?.phone === value) return true;
    if (!Boolean(value)) return true;
    return validatePhoneNumber(value, clientDetails?.contact_id);
  };

  const emailValidation = async (value) => {
    if (!Boolean(value)) return true;
    if (clientDetails?.email === value) return true;
    try {
      await emailDuplicateCheck(value);
      return true;
    } catch (error) {
      return 'Email already exists';
    }
  };

  const getFormErrorMessage = (name) => {
    return errors[name] && <InputErrorMessage>{errors[name].message}</InputErrorMessage>;
  };

  const onSubmit = (formData) => {
    // Check which fields were changed
    const isUpdateUserDetails =
      dirtyFields?.email ||
      dirtyFields?.phone ||
      dirtyFields?.street ||
      dirtyFields?.city ||
      dirtyFields?.state ||
      dirtyFields?.zip ||
      dirtyFields?.country ||
      dirtyFields?.dob ||
      dirtyFields?.is_create_client_portal;
    const isUpdateCaseDetails = dirtyFields?.is_primary_contact || dirtyFields?.is_billing_contact;

    handleUpdateContact(formData, isUpdateUserDetails, isUpdateCaseDetails);
  };

  return (
    <>
      <div className="contact-card-title mb-2">CLIENT</div>
      <Card className="card-bg">
        <div className="d-flex pb-3 align-items-center justify-content-between border-bottom filter-wraper">
          <div className="client-name mb-3 mb-lg-0 w-100 d-flex align-items-center contact-name-input">
            {isEdit ? (
              <>
                <AutoComplete
                  value={client}
                  suggestions={contactList}
                  placeholder="Search Existing Contacts"
                  completeMethod={searchAsync}
                  loading={loading}
                  itemTemplate={itemTemplate}
                  selectedItemTemplate={itemTemplate}
                  className="flex-grow-1 input-shadow w-100"
                  inputClassName="w-100"
                  onChange={(e) => setClient(e.value)}
                />
                <Button
                  icon="pi pi-check"
                  className="p-button-secondary mx-2"
                  type="button"
                  onClick={updateCaseContact}
                  disabled={typeof client !== 'object'}
                  loading={loading}
                />
                <Button
                  icon="pi pi-times"
                  className="p-button-secondary outline"
                  type="button"
                  onClick={() => {
                    setClient('');
                    toggleIsEdit();
                  }}
                  disabled={loading}
                />
              </>
            ) : (
              <>
                {clientDetails?.first_name || clientDetails?.title ? (
                  <div className="d-flex align-items-center justify-content-between w-100">
                    <div className="d-flex align-items-center" onClick={gotoContacts}>
                      <ImageComponent filePath={clientDetails.profile_image} fileName={clientDetails.first_name} fileSize="small.jpg" />
                      <a href className="contact-name-input redirection-link-no-text-transform">
                        {generateFullName(clientDetails)}
                      </a>
                      {!(clientDetails?.status === 'ACTIVE') ? (
                        <Tag className="m-1 ms-0 tag-cases border-0" value={clientDetails?.status} rounded></Tag>
                      ) : null}
                    </div>
                    <Button tooltip="Change Client" className="p-button-secondary outline" onClick={handleEditClient}>
                      Change
                    </Button>
                  </div>
                ) : (
                  <div className="d-flex align-items-center">
                    N/A {<i className="icon-edit text-primary-main pointer ms-2" onClick={handleEditClient} />}
                  </div>
                )}
              </>
            )}
          </div>
        </div>

        <div className="d-flex align-items-center py-2 flex-wrap">
          <div className="col-12 col-lg-4 p-medium">
            Email
            {watch('is_create_client_portal') === true && <label className="text-danger">*</label>}
          </div>
          <div className="col-12 col-lg-8">
            <Controller
              name="email"
              control={control}
              rules={{
                required: watch('is_create_client_portal') ? 'Email is required.' : false,
                pattern: {
                  value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                  message: 'Entered value does not match email format',
                },
                validate: { asyncValidation: (v) => emailValidation(v) },
              }}
              render={({ field }) => (
                <InputText
                  disabled={isArchivedDisabled}
                  placeholder="Email"
                  id={field.name}
                  {...field}
                  onChange={(e) => {
                    const trimmedValue = e.target.value.trim();
                    field.onChange(trimmedValue);
                    if (!e.target.value) {
                      setValue('is_create_client_portal', false);
                    }
                  }}
                  onBlur={() => trigger('email')}
                  className="input-shadow w-100"
                />
              )}
            />
            {getFormErrorMessage('email')}
          </div>
        </div>
        <div className="d-flex align-items-center py-2 flex-wrap">
          <div className="col-12 col-lg-4 p-medium">Phone Number</div>
          <div className="col-12 col-lg-8">
            <Controller
              name="phone"
              control={control}
              rules={{
                validate: { asyncValidation: (v) => validatePhone(v) },
                minLength: { value: 10, message: 'Min length should be 10' },
              }}
              render={({ field }) => (
                <NumberFormat
                  disabled={isArchivedDisabled}
                  className="input-shadow w-100 p-2 border-0 number-format p-inputtext"
                  placeholder="Type in"
                  format="(###) ###-####"
                  mask="_"
                  name="phone"
                  keyfilter={'pnum'}
                  //onValueChange={(e) => setValue('phone', e.value)}
                  onValueChange={(e) => {
                    // setValue('phone', e.value);
                    setValue('phone', e.value, { shouldDirty: true });
                    trigger('phone'); // Trigger validation on value change
                  }}
                  value={field.value}
                  //onBlur={() => trigger('phone')}
                />
              )}
            />
            {getFormErrorMessage('phone')}
          </div>
        </div>
        <div className="d-flex align-items-center py-2 flex-wrap">
          <div className="col-12 col-lg-4 p-medium">Date of Birth</div>
          <div className="col-12 col-lg-8">
            <Controller
              name="dob"
              control={control}
              render={({ field }) => (
                <Calendar
                  monthNavigator
                  yearNavigator
                  yearRange="1700:2050"
                  showButtonBar
                  placeholder="Enter a date"
                  className="w-100 input-shadow"
                  name={field.name}
                  value={field?.value ? parseDOB(field?.value) : ''}
                  onChange={(e) => {
                    const date = e.value;
                    if (date instanceof Date) {
                      field.onChange(formatDOB(date));
                    }
                  }}
                  dateFormat={'mm/dd/yy'}
                  disabled={isArchivedDisabled}
                />
              )}
            />
          </div>
        </div>
        <div className="d-flex align-items-center py-2 flex-wrap">
          <div className="col-12 col-lg-4">Street</div>
          <div className="col-12 col-lg-8">
            <Controller
              name="street"
              control={control}
              render={({ field }) => (
                <TextSnippetWrapper
                  type="text"
                  disabled={isArchivedDisabled}
                  className="input-shadow w-100"
                  placeholder="Street"
                  {...field}
                />
              )}
            />
          </div>
        </div>

        <div className="d-flex align-items-center py-2 flex-wrap">
          <div className="col-12 col-lg-4">City</div>
          <div className="col-12 col-lg-8">
            <Controller
              name="city"
              control={control}
              render={({ field }) => (
                <TextSnippetWrapper
                  type="text"
                  disabled={isArchivedDisabled}
                  className="input-shadow w-100"
                  placeholder="City"
                  {...field}
                />
              )}
            />
          </div>
        </div>

        <div className="d-flex align-items-center py-2 flex-wrap">
          <div className="col-12 col-lg-4">State</div>
          <div className="col-12 col-lg-8">
            {watch('country') === 'United States' ? (
              <div>
                <Controller
                  name="state"
                  control={control}
                  render={({ field: { onChange, value } }) => (
                    <Dropdown
                      className="input-shadow w-100"
                      placeholder="State"
                      value={value}
                      options={state_list}
                      onChange={(e) => onChange(e.value)}
                      filter
                      disabled={isArchivedDisabled}
                    />
                  )}
                />
              </div>
            ) : (
              <div>
                <Controller
                  name="state"
                  control={control}
                  render={({ field }) => (
                    <TextSnippetWrapper type="text" disabled={isArchivedDisabled} className="input-shadow w-100" {...field} />
                  )}
                />
              </div>
            )}
          </div>
        </div>

        <div className="d-flex align-items-center py-2 flex-wrap">
          <div className="col-12 col-lg-4">Zip Code</div>
          <div className="col-12 col-lg-8">
            <Controller
              name="zip"
              control={control}
              render={({ field }) => (
                <InputText disabled={isArchivedDisabled} className="input-shadow w-100" placeholder="Zip" {...field} maxLength={5} />
              )}
            />
          </div>
        </div>

        <div className="d-flex align-items-center py-2 flex-wrap">
          <div className="col-12 col-lg-4">Country</div>
          <div className="col-12 col-lg-8">
            <Controller
              name="country"
              control={control}
              defaultValue={'United States'}
              render={({ field: { onChange, value } }) => (
                <Dropdown
                  className="input-shadow w-100"
                  placeholder="Country"
                  value={value}
                  options={countriesList}
                  optionLabel="name"
                  optionValue="name"
                  onChange={(e) => onChange(e.value)}
                  disabled={value || isArchivedDisabled}
                />
              )}
            />
          </div>
        </div>
        {clientDetails?.contact_type === 'person' && (
          <div className="d-flex align-items-center py-2 flex-wrap">
            <div className="col-6 col-lg-4 p-medium" id="#detail_lawft_access">
              Enable Lawft Access
            </div>
            <div className="col-6 col-lg-8">
              <div className="" id="#detail_lawft_access">
                <Controller
                  name="is_create_client_portal"
                  control={control}
                  render={({ field: { value, onChange, name } }) => (
                    <>
                      <InputSwitch
                        checked={value}
                        name={name}
                        id={'detail_lawft_access'}
                        onChange={(e) => onChange(e.value)}
                        className={`input-switch email-validatio-tip ${!watch('email') ? ' pe-none opacity-25' : ''}`}
                        disabled={!watch('email') || isArchivedDisabled}
                      />
                      {!watch('email') && (
                        <Tooltip target={`#detail_lawft_access`} position="right" className="case-tooltip">
                          {'Add Email To Enable Lawft Access'}
                        </Tooltip>
                      )}
                    </>
                  )}
                />
              </div>
            </div>
          </div>
        )}
        {!isLeadSpecific && (
          <div className="d-flex align-items-center py-2 flex-wrap">
            <div className="col-6 col-lg-4 p-medium" id="#detail_lawft_access">
              Primary Contact
            </div>
            <div className="col-6 col-lg-8">
              <Controller
                name="is_primary_contact"
                control={control}
                render={({ field: { value, name, onChange } }) => (
                  <InputSwitch
                    checked={value}
                    name={name}
                    onChange={(e) => onChange(e.value)}
                    className="input-switch email-validatio-tip"
                  />
                )}
              />
            </div>
          </div>
        )}
        {!isLeadSpecific && (
          <div className="d-flex align-items-center py-2 flex-wrap">
            <div className="col-6 col-lg-4 p-medium" id="#detail_lawft_access">
              Billing Contact
            </div>
            <div className="col-6 col-lg-8">
              <Controller
                name="is_billing_contact"
                control={control}
                render={({ field: { value, name, onChange } }) => (
                  <InputSwitch
                    checked={value}
                    name={name}
                    onChange={(e) => onChange(e.value)}
                    className="input-switch email-validatio-tip"
                  />
                )}
              />
            </div>
          </div>
        )}
        <div className="row pe-3">
          <CustomFields field="custom_field" fieldDisable={isArchivedDisabled} control={control} setValue={setValue} watch={watch} />
        </div>
        <div className="footer-btn">
          <Button
            label="Save"
            disabled={client || (isLeadSpecific && isArchivedDisabled) || !isDirty || isSubmitting}
            className="btn-outline p-button-secondary"
            type="button"
            onClick={handleSubmit(onSubmit)}
          />
        </div>
      </Card>
    </>
  );
}
